import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getVenues,
  GetVenuesParams,
  createVenue,
  updateVenue,
  deleteVenue,
} from "@/services/venue";
import type { CreateVenueDto, UpdateVenueDto } from "@/types";

export const VENUES_QUERY_KEY = "venues";

/**
 * Hook to fetch venues with pagination
 */
export function useVenues(params?: GetVenuesParams) {
  return useQuery({
    queryKey: [VENUES_QUERY_KEY, params],
    queryFn: () => getVenues(params),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Hook to create a venue
 */
export function useCreateVenue() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateVenueDto) => createVenue(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [VENUES_QUERY_KEY] });
    },
  });
}

/**
 * Hook to update a venue
 */
export function useUpdateVenue() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateVenueDto }) =>
      updateVenue(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [VENUES_QUERY_KEY] });
    },
  });
}

/**
 * Hook to delete a venue
 */
export function useDeleteVenue() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteVenue(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [VENUES_QUERY_KEY] });
    },
  });
}

